/**
 * Version-indexed constant tables from ISO/IEC 18004 (QR Code Model 2)
 * Annex data. Fetched from the network-mirrored spec walkthrough during
 * authoring and cross-checked (each `ECC_BLOCK_TABLE` row's block counts
 * verified programmatically to sum to the table's own published total) —
 * not hand-transcribed from memory. The source pages are the "Error Correction Code
 * Words and Block Information", "Alignment Pattern Locations", and
 * "Structure Final Message" pages of the ISO/IEC 18004 walkthrough.
 */
import type { EccBlockInfo, QrErrorCorrectionLevel } from './types'

// Generated from ISO/IEC 18004 Annex data (mirrored at thonky.com/qr-code-tutorial/error-correction-table),
// fetched and cross-checked against the network source during authoring, each row's block
// arithmetic verified to sum to the table's own stated total — not hand-transcribed.
// Index 0 is unused so the array can be indexed directly by version number (1-40).
export const ECC_BLOCK_TABLE: (Record<QrErrorCorrectionLevel, EccBlockInfo> | undefined)[] = [
	undefined,
	{
		L: {
			totalDataCodewords: 19,
			ecCodewordsPerBlock: 7,
			group1Blocks: 1,
			group1DataCodewords: 19,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		M: {
			totalDataCodewords: 16,
			ecCodewordsPerBlock: 10,
			group1Blocks: 1,
			group1DataCodewords: 16,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		Q: {
			totalDataCodewords: 13,
			ecCodewordsPerBlock: 13,
			group1Blocks: 1,
			group1DataCodewords: 13,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		H: {
			totalDataCodewords: 9,
			ecCodewordsPerBlock: 17,
			group1Blocks: 1,
			group1DataCodewords: 9,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
	}, // version 1
	{
		L: {
			totalDataCodewords: 34,
			ecCodewordsPerBlock: 10,
			group1Blocks: 1,
			group1DataCodewords: 34,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		M: {
			totalDataCodewords: 28,
			ecCodewordsPerBlock: 16,
			group1Blocks: 1,
			group1DataCodewords: 28,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		Q: {
			totalDataCodewords: 22,
			ecCodewordsPerBlock: 22,
			group1Blocks: 1,
			group1DataCodewords: 22,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		H: {
			totalDataCodewords: 16,
			ecCodewordsPerBlock: 28,
			group1Blocks: 1,
			group1DataCodewords: 16,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
	}, // version 2
	{
		L: {
			totalDataCodewords: 55,
			ecCodewordsPerBlock: 15,
			group1Blocks: 1,
			group1DataCodewords: 55,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		M: {
			totalDataCodewords: 44,
			ecCodewordsPerBlock: 26,
			group1Blocks: 1,
			group1DataCodewords: 44,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		Q: {
			totalDataCodewords: 34,
			ecCodewordsPerBlock: 18,
			group1Blocks: 2,
			group1DataCodewords: 17,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		H: {
			totalDataCodewords: 26,
			ecCodewordsPerBlock: 22,
			group1Blocks: 2,
			group1DataCodewords: 13,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
	}, // version 3
	{
		L: {
			totalDataCodewords: 80,
			ecCodewordsPerBlock: 20,
			group1Blocks: 1,
			group1DataCodewords: 80,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		M: {
			totalDataCodewords: 64,
			ecCodewordsPerBlock: 18,
			group1Blocks: 2,
			group1DataCodewords: 32,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		Q: {
			totalDataCodewords: 48,
			ecCodewordsPerBlock: 26,
			group1Blocks: 2,
			group1DataCodewords: 24,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		H: {
			totalDataCodewords: 36,
			ecCodewordsPerBlock: 16,
			group1Blocks: 4,
			group1DataCodewords: 9,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
	}, // version 4
	{
		L: {
			totalDataCodewords: 108,
			ecCodewordsPerBlock: 26,
			group1Blocks: 1,
			group1DataCodewords: 108,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		M: {
			totalDataCodewords: 86,
			ecCodewordsPerBlock: 24,
			group1Blocks: 2,
			group1DataCodewords: 43,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		Q: {
			totalDataCodewords: 62,
			ecCodewordsPerBlock: 18,
			group1Blocks: 2,
			group1DataCodewords: 15,
			group2Blocks: 2,
			group2DataCodewords: 16,
		},
		H: {
			totalDataCodewords: 46,
			ecCodewordsPerBlock: 22,
			group1Blocks: 2,
			group1DataCodewords: 11,
			group2Blocks: 2,
			group2DataCodewords: 12,
		},
	}, // version 5
	{
		L: {
			totalDataCodewords: 136,
			ecCodewordsPerBlock: 18,
			group1Blocks: 2,
			group1DataCodewords: 68,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		M: {
			totalDataCodewords: 108,
			ecCodewordsPerBlock: 16,
			group1Blocks: 4,
			group1DataCodewords: 27,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		Q: {
			totalDataCodewords: 76,
			ecCodewordsPerBlock: 24,
			group1Blocks: 4,
			group1DataCodewords: 19,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		H: {
			totalDataCodewords: 60,
			ecCodewordsPerBlock: 28,
			group1Blocks: 4,
			group1DataCodewords: 15,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
	}, // version 6
	{
		L: {
			totalDataCodewords: 156,
			ecCodewordsPerBlock: 20,
			group1Blocks: 2,
			group1DataCodewords: 78,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		M: {
			totalDataCodewords: 124,
			ecCodewordsPerBlock: 18,
			group1Blocks: 4,
			group1DataCodewords: 31,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		Q: {
			totalDataCodewords: 88,
			ecCodewordsPerBlock: 18,
			group1Blocks: 2,
			group1DataCodewords: 14,
			group2Blocks: 4,
			group2DataCodewords: 15,
		},
		H: {
			totalDataCodewords: 66,
			ecCodewordsPerBlock: 26,
			group1Blocks: 4,
			group1DataCodewords: 13,
			group2Blocks: 1,
			group2DataCodewords: 14,
		},
	}, // version 7
	{
		L: {
			totalDataCodewords: 194,
			ecCodewordsPerBlock: 24,
			group1Blocks: 2,
			group1DataCodewords: 97,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		M: {
			totalDataCodewords: 154,
			ecCodewordsPerBlock: 22,
			group1Blocks: 2,
			group1DataCodewords: 38,
			group2Blocks: 2,
			group2DataCodewords: 39,
		},
		Q: {
			totalDataCodewords: 110,
			ecCodewordsPerBlock: 22,
			group1Blocks: 4,
			group1DataCodewords: 18,
			group2Blocks: 2,
			group2DataCodewords: 19,
		},
		H: {
			totalDataCodewords: 86,
			ecCodewordsPerBlock: 26,
			group1Blocks: 4,
			group1DataCodewords: 14,
			group2Blocks: 2,
			group2DataCodewords: 15,
		},
	}, // version 8
	{
		L: {
			totalDataCodewords: 232,
			ecCodewordsPerBlock: 30,
			group1Blocks: 2,
			group1DataCodewords: 116,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		M: {
			totalDataCodewords: 182,
			ecCodewordsPerBlock: 22,
			group1Blocks: 3,
			group1DataCodewords: 36,
			group2Blocks: 2,
			group2DataCodewords: 37,
		},
		Q: {
			totalDataCodewords: 132,
			ecCodewordsPerBlock: 20,
			group1Blocks: 4,
			group1DataCodewords: 16,
			group2Blocks: 4,
			group2DataCodewords: 17,
		},
		H: {
			totalDataCodewords: 100,
			ecCodewordsPerBlock: 24,
			group1Blocks: 4,
			group1DataCodewords: 12,
			group2Blocks: 4,
			group2DataCodewords: 13,
		},
	}, // version 9
	{
		L: {
			totalDataCodewords: 274,
			ecCodewordsPerBlock: 18,
			group1Blocks: 2,
			group1DataCodewords: 68,
			group2Blocks: 2,
			group2DataCodewords: 69,
		},
		M: {
			totalDataCodewords: 216,
			ecCodewordsPerBlock: 26,
			group1Blocks: 4,
			group1DataCodewords: 43,
			group2Blocks: 1,
			group2DataCodewords: 44,
		},
		Q: {
			totalDataCodewords: 154,
			ecCodewordsPerBlock: 24,
			group1Blocks: 6,
			group1DataCodewords: 19,
			group2Blocks: 2,
			group2DataCodewords: 20,
		},
		H: {
			totalDataCodewords: 122,
			ecCodewordsPerBlock: 28,
			group1Blocks: 6,
			group1DataCodewords: 15,
			group2Blocks: 2,
			group2DataCodewords: 16,
		},
	}, // version 10
	{
		L: {
			totalDataCodewords: 324,
			ecCodewordsPerBlock: 20,
			group1Blocks: 4,
			group1DataCodewords: 81,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		M: {
			totalDataCodewords: 254,
			ecCodewordsPerBlock: 30,
			group1Blocks: 1,
			group1DataCodewords: 50,
			group2Blocks: 4,
			group2DataCodewords: 51,
		},
		Q: {
			totalDataCodewords: 180,
			ecCodewordsPerBlock: 28,
			group1Blocks: 4,
			group1DataCodewords: 22,
			group2Blocks: 4,
			group2DataCodewords: 23,
		},
		H: {
			totalDataCodewords: 140,
			ecCodewordsPerBlock: 24,
			group1Blocks: 3,
			group1DataCodewords: 12,
			group2Blocks: 8,
			group2DataCodewords: 13,
		},
	}, // version 11
	{
		L: {
			totalDataCodewords: 370,
			ecCodewordsPerBlock: 24,
			group1Blocks: 2,
			group1DataCodewords: 92,
			group2Blocks: 2,
			group2DataCodewords: 93,
		},
		M: {
			totalDataCodewords: 290,
			ecCodewordsPerBlock: 22,
			group1Blocks: 6,
			group1DataCodewords: 36,
			group2Blocks: 2,
			group2DataCodewords: 37,
		},
		Q: {
			totalDataCodewords: 206,
			ecCodewordsPerBlock: 26,
			group1Blocks: 4,
			group1DataCodewords: 20,
			group2Blocks: 6,
			group2DataCodewords: 21,
		},
		H: {
			totalDataCodewords: 158,
			ecCodewordsPerBlock: 28,
			group1Blocks: 7,
			group1DataCodewords: 14,
			group2Blocks: 4,
			group2DataCodewords: 15,
		},
	}, // version 12
	{
		L: {
			totalDataCodewords: 428,
			ecCodewordsPerBlock: 26,
			group1Blocks: 4,
			group1DataCodewords: 107,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		M: {
			totalDataCodewords: 334,
			ecCodewordsPerBlock: 22,
			group1Blocks: 8,
			group1DataCodewords: 37,
			group2Blocks: 1,
			group2DataCodewords: 38,
		},
		Q: {
			totalDataCodewords: 244,
			ecCodewordsPerBlock: 24,
			group1Blocks: 8,
			group1DataCodewords: 20,
			group2Blocks: 4,
			group2DataCodewords: 21,
		},
		H: {
			totalDataCodewords: 180,
			ecCodewordsPerBlock: 22,
			group1Blocks: 12,
			group1DataCodewords: 11,
			group2Blocks: 4,
			group2DataCodewords: 12,
		},
	}, // version 13
	{
		L: {
			totalDataCodewords: 461,
			ecCodewordsPerBlock: 30,
			group1Blocks: 3,
			group1DataCodewords: 115,
			group2Blocks: 1,
			group2DataCodewords: 116,
		},
		M: {
			totalDataCodewords: 365,
			ecCodewordsPerBlock: 24,
			group1Blocks: 4,
			group1DataCodewords: 40,
			group2Blocks: 5,
			group2DataCodewords: 41,
		},
		Q: {
			totalDataCodewords: 261,
			ecCodewordsPerBlock: 20,
			group1Blocks: 11,
			group1DataCodewords: 16,
			group2Blocks: 5,
			group2DataCodewords: 17,
		},
		H: {
			totalDataCodewords: 197,
			ecCodewordsPerBlock: 24,
			group1Blocks: 11,
			group1DataCodewords: 12,
			group2Blocks: 5,
			group2DataCodewords: 13,
		},
	}, // version 14
	{
		L: {
			totalDataCodewords: 523,
			ecCodewordsPerBlock: 22,
			group1Blocks: 5,
			group1DataCodewords: 87,
			group2Blocks: 1,
			group2DataCodewords: 88,
		},
		M: {
			totalDataCodewords: 415,
			ecCodewordsPerBlock: 24,
			group1Blocks: 5,
			group1DataCodewords: 41,
			group2Blocks: 5,
			group2DataCodewords: 42,
		},
		Q: {
			totalDataCodewords: 295,
			ecCodewordsPerBlock: 30,
			group1Blocks: 5,
			group1DataCodewords: 24,
			group2Blocks: 7,
			group2DataCodewords: 25,
		},
		H: {
			totalDataCodewords: 223,
			ecCodewordsPerBlock: 24,
			group1Blocks: 11,
			group1DataCodewords: 12,
			group2Blocks: 7,
			group2DataCodewords: 13,
		},
	}, // version 15
	{
		L: {
			totalDataCodewords: 589,
			ecCodewordsPerBlock: 24,
			group1Blocks: 5,
			group1DataCodewords: 98,
			group2Blocks: 1,
			group2DataCodewords: 99,
		},
		M: {
			totalDataCodewords: 453,
			ecCodewordsPerBlock: 28,
			group1Blocks: 7,
			group1DataCodewords: 45,
			group2Blocks: 3,
			group2DataCodewords: 46,
		},
		Q: {
			totalDataCodewords: 325,
			ecCodewordsPerBlock: 24,
			group1Blocks: 15,
			group1DataCodewords: 19,
			group2Blocks: 2,
			group2DataCodewords: 20,
		},
		H: {
			totalDataCodewords: 253,
			ecCodewordsPerBlock: 30,
			group1Blocks: 3,
			group1DataCodewords: 15,
			group2Blocks: 13,
			group2DataCodewords: 16,
		},
	}, // version 16
	{
		L: {
			totalDataCodewords: 647,
			ecCodewordsPerBlock: 28,
			group1Blocks: 1,
			group1DataCodewords: 107,
			group2Blocks: 5,
			group2DataCodewords: 108,
		},
		M: {
			totalDataCodewords: 507,
			ecCodewordsPerBlock: 28,
			group1Blocks: 10,
			group1DataCodewords: 46,
			group2Blocks: 1,
			group2DataCodewords: 47,
		},
		Q: {
			totalDataCodewords: 367,
			ecCodewordsPerBlock: 28,
			group1Blocks: 1,
			group1DataCodewords: 22,
			group2Blocks: 15,
			group2DataCodewords: 23,
		},
		H: {
			totalDataCodewords: 283,
			ecCodewordsPerBlock: 28,
			group1Blocks: 2,
			group1DataCodewords: 14,
			group2Blocks: 17,
			group2DataCodewords: 15,
		},
	}, // version 17
	{
		L: {
			totalDataCodewords: 721,
			ecCodewordsPerBlock: 30,
			group1Blocks: 5,
			group1DataCodewords: 120,
			group2Blocks: 1,
			group2DataCodewords: 121,
		},
		M: {
			totalDataCodewords: 563,
			ecCodewordsPerBlock: 26,
			group1Blocks: 9,
			group1DataCodewords: 43,
			group2Blocks: 4,
			group2DataCodewords: 44,
		},
		Q: {
			totalDataCodewords: 397,
			ecCodewordsPerBlock: 28,
			group1Blocks: 17,
			group1DataCodewords: 22,
			group2Blocks: 1,
			group2DataCodewords: 23,
		},
		H: {
			totalDataCodewords: 313,
			ecCodewordsPerBlock: 28,
			group1Blocks: 2,
			group1DataCodewords: 14,
			group2Blocks: 19,
			group2DataCodewords: 15,
		},
	}, // version 18
	{
		L: {
			totalDataCodewords: 795,
			ecCodewordsPerBlock: 28,
			group1Blocks: 3,
			group1DataCodewords: 113,
			group2Blocks: 4,
			group2DataCodewords: 114,
		},
		M: {
			totalDataCodewords: 627,
			ecCodewordsPerBlock: 26,
			group1Blocks: 3,
			group1DataCodewords: 44,
			group2Blocks: 11,
			group2DataCodewords: 45,
		},
		Q: {
			totalDataCodewords: 445,
			ecCodewordsPerBlock: 26,
			group1Blocks: 17,
			group1DataCodewords: 21,
			group2Blocks: 4,
			group2DataCodewords: 22,
		},
		H: {
			totalDataCodewords: 341,
			ecCodewordsPerBlock: 26,
			group1Blocks: 9,
			group1DataCodewords: 13,
			group2Blocks: 16,
			group2DataCodewords: 14,
		},
	}, // version 19
	{
		L: {
			totalDataCodewords: 861,
			ecCodewordsPerBlock: 28,
			group1Blocks: 3,
			group1DataCodewords: 107,
			group2Blocks: 5,
			group2DataCodewords: 108,
		},
		M: {
			totalDataCodewords: 669,
			ecCodewordsPerBlock: 26,
			group1Blocks: 3,
			group1DataCodewords: 41,
			group2Blocks: 13,
			group2DataCodewords: 42,
		},
		Q: {
			totalDataCodewords: 485,
			ecCodewordsPerBlock: 30,
			group1Blocks: 15,
			group1DataCodewords: 24,
			group2Blocks: 5,
			group2DataCodewords: 25,
		},
		H: {
			totalDataCodewords: 385,
			ecCodewordsPerBlock: 28,
			group1Blocks: 15,
			group1DataCodewords: 15,
			group2Blocks: 10,
			group2DataCodewords: 16,
		},
	}, // version 20
	{
		L: {
			totalDataCodewords: 932,
			ecCodewordsPerBlock: 28,
			group1Blocks: 4,
			group1DataCodewords: 116,
			group2Blocks: 4,
			group2DataCodewords: 117,
		},
		M: {
			totalDataCodewords: 714,
			ecCodewordsPerBlock: 26,
			group1Blocks: 17,
			group1DataCodewords: 42,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		Q: {
			totalDataCodewords: 512,
			ecCodewordsPerBlock: 28,
			group1Blocks: 17,
			group1DataCodewords: 22,
			group2Blocks: 6,
			group2DataCodewords: 23,
		},
		H: {
			totalDataCodewords: 406,
			ecCodewordsPerBlock: 30,
			group1Blocks: 19,
			group1DataCodewords: 16,
			group2Blocks: 6,
			group2DataCodewords: 17,
		},
	}, // version 21
	{
		L: {
			totalDataCodewords: 1006,
			ecCodewordsPerBlock: 28,
			group1Blocks: 2,
			group1DataCodewords: 111,
			group2Blocks: 7,
			group2DataCodewords: 112,
		},
		M: {
			totalDataCodewords: 782,
			ecCodewordsPerBlock: 28,
			group1Blocks: 17,
			group1DataCodewords: 46,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		Q: {
			totalDataCodewords: 568,
			ecCodewordsPerBlock: 30,
			group1Blocks: 7,
			group1DataCodewords: 24,
			group2Blocks: 16,
			group2DataCodewords: 25,
		},
		H: {
			totalDataCodewords: 442,
			ecCodewordsPerBlock: 24,
			group1Blocks: 34,
			group1DataCodewords: 13,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
	}, // version 22
	{
		L: {
			totalDataCodewords: 1094,
			ecCodewordsPerBlock: 30,
			group1Blocks: 4,
			group1DataCodewords: 121,
			group2Blocks: 5,
			group2DataCodewords: 122,
		},
		M: {
			totalDataCodewords: 860,
			ecCodewordsPerBlock: 28,
			group1Blocks: 4,
			group1DataCodewords: 47,
			group2Blocks: 14,
			group2DataCodewords: 48,
		},
		Q: {
			totalDataCodewords: 614,
			ecCodewordsPerBlock: 30,
			group1Blocks: 11,
			group1DataCodewords: 24,
			group2Blocks: 14,
			group2DataCodewords: 25,
		},
		H: {
			totalDataCodewords: 464,
			ecCodewordsPerBlock: 30,
			group1Blocks: 16,
			group1DataCodewords: 15,
			group2Blocks: 14,
			group2DataCodewords: 16,
		},
	}, // version 23
	{
		L: {
			totalDataCodewords: 1174,
			ecCodewordsPerBlock: 30,
			group1Blocks: 6,
			group1DataCodewords: 117,
			group2Blocks: 4,
			group2DataCodewords: 118,
		},
		M: {
			totalDataCodewords: 914,
			ecCodewordsPerBlock: 28,
			group1Blocks: 6,
			group1DataCodewords: 45,
			group2Blocks: 14,
			group2DataCodewords: 46,
		},
		Q: {
			totalDataCodewords: 664,
			ecCodewordsPerBlock: 30,
			group1Blocks: 11,
			group1DataCodewords: 24,
			group2Blocks: 16,
			group2DataCodewords: 25,
		},
		H: {
			totalDataCodewords: 514,
			ecCodewordsPerBlock: 30,
			group1Blocks: 30,
			group1DataCodewords: 16,
			group2Blocks: 2,
			group2DataCodewords: 17,
		},
	}, // version 24
	{
		L: {
			totalDataCodewords: 1276,
			ecCodewordsPerBlock: 26,
			group1Blocks: 8,
			group1DataCodewords: 106,
			group2Blocks: 4,
			group2DataCodewords: 107,
		},
		M: {
			totalDataCodewords: 1000,
			ecCodewordsPerBlock: 28,
			group1Blocks: 8,
			group1DataCodewords: 47,
			group2Blocks: 13,
			group2DataCodewords: 48,
		},
		Q: {
			totalDataCodewords: 718,
			ecCodewordsPerBlock: 30,
			group1Blocks: 7,
			group1DataCodewords: 24,
			group2Blocks: 22,
			group2DataCodewords: 25,
		},
		H: {
			totalDataCodewords: 538,
			ecCodewordsPerBlock: 30,
			group1Blocks: 22,
			group1DataCodewords: 15,
			group2Blocks: 13,
			group2DataCodewords: 16,
		},
	}, // version 25
	{
		L: {
			totalDataCodewords: 1370,
			ecCodewordsPerBlock: 28,
			group1Blocks: 10,
			group1DataCodewords: 114,
			group2Blocks: 2,
			group2DataCodewords: 115,
		},
		M: {
			totalDataCodewords: 1062,
			ecCodewordsPerBlock: 28,
			group1Blocks: 19,
			group1DataCodewords: 46,
			group2Blocks: 4,
			group2DataCodewords: 47,
		},
		Q: {
			totalDataCodewords: 754,
			ecCodewordsPerBlock: 28,
			group1Blocks: 28,
			group1DataCodewords: 22,
			group2Blocks: 6,
			group2DataCodewords: 23,
		},
		H: {
			totalDataCodewords: 596,
			ecCodewordsPerBlock: 30,
			group1Blocks: 33,
			group1DataCodewords: 16,
			group2Blocks: 4,
			group2DataCodewords: 17,
		},
	}, // version 26
	{
		L: {
			totalDataCodewords: 1468,
			ecCodewordsPerBlock: 30,
			group1Blocks: 8,
			group1DataCodewords: 122,
			group2Blocks: 4,
			group2DataCodewords: 123,
		},
		M: {
			totalDataCodewords: 1128,
			ecCodewordsPerBlock: 28,
			group1Blocks: 22,
			group1DataCodewords: 45,
			group2Blocks: 3,
			group2DataCodewords: 46,
		},
		Q: {
			totalDataCodewords: 808,
			ecCodewordsPerBlock: 30,
			group1Blocks: 8,
			group1DataCodewords: 23,
			group2Blocks: 26,
			group2DataCodewords: 24,
		},
		H: {
			totalDataCodewords: 628,
			ecCodewordsPerBlock: 30,
			group1Blocks: 12,
			group1DataCodewords: 15,
			group2Blocks: 28,
			group2DataCodewords: 16,
		},
	}, // version 27
	{
		L: {
			totalDataCodewords: 1531,
			ecCodewordsPerBlock: 30,
			group1Blocks: 3,
			group1DataCodewords: 117,
			group2Blocks: 10,
			group2DataCodewords: 118,
		},
		M: {
			totalDataCodewords: 1193,
			ecCodewordsPerBlock: 28,
			group1Blocks: 3,
			group1DataCodewords: 45,
			group2Blocks: 23,
			group2DataCodewords: 46,
		},
		Q: {
			totalDataCodewords: 871,
			ecCodewordsPerBlock: 30,
			group1Blocks: 4,
			group1DataCodewords: 24,
			group2Blocks: 31,
			group2DataCodewords: 25,
		},
		H: {
			totalDataCodewords: 661,
			ecCodewordsPerBlock: 30,
			group1Blocks: 11,
			group1DataCodewords: 15,
			group2Blocks: 31,
			group2DataCodewords: 16,
		},
	}, // version 28
	{
		L: {
			totalDataCodewords: 1631,
			ecCodewordsPerBlock: 30,
			group1Blocks: 7,
			group1DataCodewords: 116,
			group2Blocks: 7,
			group2DataCodewords: 117,
		},
		M: {
			totalDataCodewords: 1267,
			ecCodewordsPerBlock: 28,
			group1Blocks: 21,
			group1DataCodewords: 45,
			group2Blocks: 7,
			group2DataCodewords: 46,
		},
		Q: {
			totalDataCodewords: 911,
			ecCodewordsPerBlock: 30,
			group1Blocks: 1,
			group1DataCodewords: 23,
			group2Blocks: 37,
			group2DataCodewords: 24,
		},
		H: {
			totalDataCodewords: 701,
			ecCodewordsPerBlock: 30,
			group1Blocks: 19,
			group1DataCodewords: 15,
			group2Blocks: 26,
			group2DataCodewords: 16,
		},
	}, // version 29
	{
		L: {
			totalDataCodewords: 1735,
			ecCodewordsPerBlock: 30,
			group1Blocks: 5,
			group1DataCodewords: 115,
			group2Blocks: 10,
			group2DataCodewords: 116,
		},
		M: {
			totalDataCodewords: 1373,
			ecCodewordsPerBlock: 28,
			group1Blocks: 19,
			group1DataCodewords: 47,
			group2Blocks: 10,
			group2DataCodewords: 48,
		},
		Q: {
			totalDataCodewords: 985,
			ecCodewordsPerBlock: 30,
			group1Blocks: 15,
			group1DataCodewords: 24,
			group2Blocks: 25,
			group2DataCodewords: 25,
		},
		H: {
			totalDataCodewords: 745,
			ecCodewordsPerBlock: 30,
			group1Blocks: 23,
			group1DataCodewords: 15,
			group2Blocks: 25,
			group2DataCodewords: 16,
		},
	}, // version 30
	{
		L: {
			totalDataCodewords: 1843,
			ecCodewordsPerBlock: 30,
			group1Blocks: 13,
			group1DataCodewords: 115,
			group2Blocks: 3,
			group2DataCodewords: 116,
		},
		M: {
			totalDataCodewords: 1455,
			ecCodewordsPerBlock: 28,
			group1Blocks: 2,
			group1DataCodewords: 46,
			group2Blocks: 29,
			group2DataCodewords: 47,
		},
		Q: {
			totalDataCodewords: 1033,
			ecCodewordsPerBlock: 30,
			group1Blocks: 42,
			group1DataCodewords: 24,
			group2Blocks: 1,
			group2DataCodewords: 25,
		},
		H: {
			totalDataCodewords: 793,
			ecCodewordsPerBlock: 30,
			group1Blocks: 23,
			group1DataCodewords: 15,
			group2Blocks: 28,
			group2DataCodewords: 16,
		},
	}, // version 31
	{
		L: {
			totalDataCodewords: 1955,
			ecCodewordsPerBlock: 30,
			group1Blocks: 17,
			group1DataCodewords: 115,
			group2Blocks: 0,
			group2DataCodewords: 0,
		},
		M: {
			totalDataCodewords: 1541,
			ecCodewordsPerBlock: 28,
			group1Blocks: 10,
			group1DataCodewords: 46,
			group2Blocks: 23,
			group2DataCodewords: 47,
		},
		Q: {
			totalDataCodewords: 1115,
			ecCodewordsPerBlock: 30,
			group1Blocks: 10,
			group1DataCodewords: 24,
			group2Blocks: 35,
			group2DataCodewords: 25,
		},
		H: {
			totalDataCodewords: 845,
			ecCodewordsPerBlock: 30,
			group1Blocks: 19,
			group1DataCodewords: 15,
			group2Blocks: 35,
			group2DataCodewords: 16,
		},
	}, // version 32
	{
		L: {
			totalDataCodewords: 2071,
			ecCodewordsPerBlock: 30,
			group1Blocks: 17,
			group1DataCodewords: 115,
			group2Blocks: 1,
			group2DataCodewords: 116,
		},
		M: {
			totalDataCodewords: 1631,
			ecCodewordsPerBlock: 28,
			group1Blocks: 14,
			group1DataCodewords: 46,
			group2Blocks: 21,
			group2DataCodewords: 47,
		},
		Q: {
			totalDataCodewords: 1171,
			ecCodewordsPerBlock: 30,
			group1Blocks: 29,
			group1DataCodewords: 24,
			group2Blocks: 19,
			group2DataCodewords: 25,
		},
		H: {
			totalDataCodewords: 901,
			ecCodewordsPerBlock: 30,
			group1Blocks: 11,
			group1DataCodewords: 15,
			group2Blocks: 46,
			group2DataCodewords: 16,
		},
	}, // version 33
	{
		L: {
			totalDataCodewords: 2191,
			ecCodewordsPerBlock: 30,
			group1Blocks: 13,
			group1DataCodewords: 115,
			group2Blocks: 6,
			group2DataCodewords: 116,
		},
		M: {
			totalDataCodewords: 1725,
			ecCodewordsPerBlock: 28,
			group1Blocks: 14,
			group1DataCodewords: 46,
			group2Blocks: 23,
			group2DataCodewords: 47,
		},
		Q: {
			totalDataCodewords: 1231,
			ecCodewordsPerBlock: 30,
			group1Blocks: 44,
			group1DataCodewords: 24,
			group2Blocks: 7,
			group2DataCodewords: 25,
		},
		H: {
			totalDataCodewords: 961,
			ecCodewordsPerBlock: 30,
			group1Blocks: 59,
			group1DataCodewords: 16,
			group2Blocks: 1,
			group2DataCodewords: 17,
		},
	}, // version 34
	{
		L: {
			totalDataCodewords: 2306,
			ecCodewordsPerBlock: 30,
			group1Blocks: 12,
			group1DataCodewords: 121,
			group2Blocks: 7,
			group2DataCodewords: 122,
		},
		M: {
			totalDataCodewords: 1812,
			ecCodewordsPerBlock: 28,
			group1Blocks: 12,
			group1DataCodewords: 47,
			group2Blocks: 26,
			group2DataCodewords: 48,
		},
		Q: {
			totalDataCodewords: 1286,
			ecCodewordsPerBlock: 30,
			group1Blocks: 39,
			group1DataCodewords: 24,
			group2Blocks: 14,
			group2DataCodewords: 25,
		},
		H: {
			totalDataCodewords: 986,
			ecCodewordsPerBlock: 30,
			group1Blocks: 22,
			group1DataCodewords: 15,
			group2Blocks: 41,
			group2DataCodewords: 16,
		},
	}, // version 35
	{
		L: {
			totalDataCodewords: 2434,
			ecCodewordsPerBlock: 30,
			group1Blocks: 6,
			group1DataCodewords: 121,
			group2Blocks: 14,
			group2DataCodewords: 122,
		},
		M: {
			totalDataCodewords: 1914,
			ecCodewordsPerBlock: 28,
			group1Blocks: 6,
			group1DataCodewords: 47,
			group2Blocks: 34,
			group2DataCodewords: 48,
		},
		Q: {
			totalDataCodewords: 1354,
			ecCodewordsPerBlock: 30,
			group1Blocks: 46,
			group1DataCodewords: 24,
			group2Blocks: 10,
			group2DataCodewords: 25,
		},
		H: {
			totalDataCodewords: 1054,
			ecCodewordsPerBlock: 30,
			group1Blocks: 2,
			group1DataCodewords: 15,
			group2Blocks: 64,
			group2DataCodewords: 16,
		},
	}, // version 36
	{
		L: {
			totalDataCodewords: 2566,
			ecCodewordsPerBlock: 30,
			group1Blocks: 17,
			group1DataCodewords: 122,
			group2Blocks: 4,
			group2DataCodewords: 123,
		},
		M: {
			totalDataCodewords: 1992,
			ecCodewordsPerBlock: 28,
			group1Blocks: 29,
			group1DataCodewords: 46,
			group2Blocks: 14,
			group2DataCodewords: 47,
		},
		Q: {
			totalDataCodewords: 1426,
			ecCodewordsPerBlock: 30,
			group1Blocks: 49,
			group1DataCodewords: 24,
			group2Blocks: 10,
			group2DataCodewords: 25,
		},
		H: {
			totalDataCodewords: 1096,
			ecCodewordsPerBlock: 30,
			group1Blocks: 24,
			group1DataCodewords: 15,
			group2Blocks: 46,
			group2DataCodewords: 16,
		},
	}, // version 37
	{
		L: {
			totalDataCodewords: 2702,
			ecCodewordsPerBlock: 30,
			group1Blocks: 4,
			group1DataCodewords: 122,
			group2Blocks: 18,
			group2DataCodewords: 123,
		},
		M: {
			totalDataCodewords: 2102,
			ecCodewordsPerBlock: 28,
			group1Blocks: 13,
			group1DataCodewords: 46,
			group2Blocks: 32,
			group2DataCodewords: 47,
		},
		Q: {
			totalDataCodewords: 1502,
			ecCodewordsPerBlock: 30,
			group1Blocks: 48,
			group1DataCodewords: 24,
			group2Blocks: 14,
			group2DataCodewords: 25,
		},
		H: {
			totalDataCodewords: 1142,
			ecCodewordsPerBlock: 30,
			group1Blocks: 42,
			group1DataCodewords: 15,
			group2Blocks: 32,
			group2DataCodewords: 16,
		},
	}, // version 38
	{
		L: {
			totalDataCodewords: 2812,
			ecCodewordsPerBlock: 30,
			group1Blocks: 20,
			group1DataCodewords: 117,
			group2Blocks: 4,
			group2DataCodewords: 118,
		},
		M: {
			totalDataCodewords: 2216,
			ecCodewordsPerBlock: 28,
			group1Blocks: 40,
			group1DataCodewords: 47,
			group2Blocks: 7,
			group2DataCodewords: 48,
		},
		Q: {
			totalDataCodewords: 1582,
			ecCodewordsPerBlock: 30,
			group1Blocks: 43,
			group1DataCodewords: 24,
			group2Blocks: 22,
			group2DataCodewords: 25,
		},
		H: {
			totalDataCodewords: 1222,
			ecCodewordsPerBlock: 30,
			group1Blocks: 10,
			group1DataCodewords: 15,
			group2Blocks: 67,
			group2DataCodewords: 16,
		},
	}, // version 39
	{
		L: {
			totalDataCodewords: 2956,
			ecCodewordsPerBlock: 30,
			group1Blocks: 19,
			group1DataCodewords: 118,
			group2Blocks: 6,
			group2DataCodewords: 119,
		},
		M: {
			totalDataCodewords: 2334,
			ecCodewordsPerBlock: 28,
			group1Blocks: 18,
			group1DataCodewords: 47,
			group2Blocks: 31,
			group2DataCodewords: 48,
		},
		Q: {
			totalDataCodewords: 1666,
			ecCodewordsPerBlock: 30,
			group1Blocks: 34,
			group1DataCodewords: 24,
			group2Blocks: 34,
			group2DataCodewords: 25,
		},
		H: {
			totalDataCodewords: 1276,
			ecCodewordsPerBlock: 30,
			group1Blocks: 20,
			group1DataCodewords: 15,
			group2Blocks: 61,
			group2DataCodewords: 16,
		},
	}, // version 40
]

// Generated from thonky.com/qr-code-tutorial/alignment-pattern-locations (mirrors ISO/IEC 18004 Table E.1),
// fetched during authoring — not hand-transcribed. Version 1 has no alignment patterns.
// Index 0 is unused so the array can be indexed directly by version number (1-40).
export const ALIGNMENT_PATTERN_POSITIONS: readonly (readonly number[])[] = [
	[],
	[], // version 1
	[6, 18], // version 2
	[6, 22], // version 3
	[6, 26], // version 4
	[6, 30], // version 5
	[6, 34], // version 6
	[6, 22, 38], // version 7
	[6, 24, 42], // version 8
	[6, 26, 46], // version 9
	[6, 28, 50], // version 10
	[6, 30, 54], // version 11
	[6, 32, 58], // version 12
	[6, 34, 62], // version 13
	[6, 26, 46, 66], // version 14
	[6, 26, 48, 70], // version 15
	[6, 26, 50, 74], // version 16
	[6, 30, 54, 78], // version 17
	[6, 30, 56, 82], // version 18
	[6, 30, 58, 86], // version 19
	[6, 34, 62, 90], // version 20
	[6, 28, 50, 72, 94], // version 21
	[6, 26, 50, 74, 98], // version 22
	[6, 30, 54, 78, 102], // version 23
	[6, 28, 54, 80, 106], // version 24
	[6, 32, 58, 84, 110], // version 25
	[6, 30, 58, 86, 114], // version 26
	[6, 34, 62, 90, 118], // version 27
	[6, 26, 50, 74, 98, 122], // version 28
	[6, 30, 54, 78, 102, 126], // version 29
	[6, 26, 52, 78, 104, 130], // version 30
	[6, 30, 56, 82, 108, 134], // version 31
	[6, 34, 60, 86, 112, 138], // version 32
	[6, 30, 58, 86, 114, 142], // version 33
	[6, 34, 62, 90, 118, 146], // version 34
	[6, 30, 54, 78, 102, 126, 150], // version 35
	[6, 24, 50, 76, 102, 128, 154], // version 36
	[6, 28, 54, 80, 106, 132, 158], // version 37
	[6, 32, 58, 84, 110, 136, 162], // version 38
	[6, 26, 54, 82, 110, 138, 166], // version 39
	[6, 30, 58, 86, 114, 142, 170], // version 40
]

// Generated from thonky.com/qr-code-tutorial/structure-final-message (mirrors ISO/IEC 18004 Table 1),
// fetched during authoring — not hand-transcribed.
// Index 0 is unused so the array can be indexed directly by version number (1-40).
export const REMAINDER_BITS: readonly number[] = [
	0, 0, 7, 7, 7, 7, 7, 0, 0, 0, 0, 0, 0, 0, 3, 3, 3, 3, 3, 3, 3, 4, 4, 4, 4, 4, 4, 4, 3, 3, 3, 3, 3, 3, 3, 0, 0, 0, 0,
	0, 0,
]
